import type { User } from "@/Modules/Models/Domain/User";
import { NotificationType } from "@/Modules/App/Models/Domain/NotificationType";
import { ApiErrorCodes } from "@/Modules/App/Models/Api/ApiErrorCodes";

import { getCustomerById } from "@/Services/TherapyApi/Customers";
import { getAgreements } from "@/Services/TherapyApi/LegalAgreements";
import { getPlan } from "@/Services/TherapyApi/Plans";
import { getSubscription } from "@/Services/TherapyApi/Subscriptions";

import SubscriptionTransformer, {
  type SubscriptionTransformerType,
} from "@/Modules/Transformers/Subscription";
import PlanTransformer from "@/Modules/Transformers/Plan";
import UserApiToUserTransformer from "@/Modules/Transformers/User/UserApiToUserTransformer";

import useUserStore from "@/Modules/User/Store";
import useCustomerStore from "@/Modules/Customers/Store";

import { hasValue, isObject } from "@/Utils/Objects";
import { isApiError } from "@/Services/Api/Typeguards/isApiError";
import { isValidString } from "@/Utils/String";

async function fetchCustomerAH(customerId: string) {
  const customerStore = useCustomerStore();

  if (customerStore.customers[customerId]) {
    customerStore.selected = customerStore.customers[customerId];

    return;
  }

  const customer = await getCustomer(customerId);

  if (customer) {
    await loadLegalAgreements(customerId, customer);
    await loadSubscription(customerId);
    await loadCurrentPlan(customerId);
  }
}

async function getCustomer(customerId: string): Promise<User | undefined> {
  try {
    const customerApi = await getCustomerById(customerId);
    const userApiToUserTransfomer = new UserApiToUserTransformer();

    if (hasValue(customerApi)) {
      return userApiToUserTransfomer.transform(customerApi);
    }
  } catch (error: unknown) {
    handleGenericError();
  }
}

async function loadLegalAgreements(customerId: string, customer: User): Promise<void> {
  try {
    const customerStore = useCustomerStore();
    const userStore = useUserStore();

    const userId = userStore.user ? userStore.user.id : "";
    const legalAgrementsApi = await getAgreements(customerId, userId);
    const legalAgreements = legalAgrementsApi ? legalAgrementsApi : [];

    if (hasValue(customerStore.customers) && isObject(customer) && hasValue(legalAgreements)) {
      customerStore.customers[customerId] = {
        ...customer,
        legalAgreements,
      };
      customerStore.selected = customerStore.customers[customerId];
    }
  } catch (error: unknown) {
    handleGenericError();
  }
}

async function loadSubscription(customerId: string): Promise<void> {
  const customerStore = useCustomerStore();
  const subscriptionTransformer: SubscriptionTransformerType = SubscriptionTransformer;

  try {
    const subscriptionApi = await getSubscription(customerId);
    const subscription = subscriptionTransformer.toDomain(subscriptionApi);

    if (subscription) {
      customerStore.customers[customerId]!.subscription = subscription;
      customerStore.selected = customerStore.customers[customerId]!;
    }
  } catch (error: unknown) {
    const code = isApiError(error) ? error["error_code"] : null;

    if (code === ApiErrorCodes.CUSTOMER_HAS_NOT_SUBSCRIPTION) {
      customerStore.isHasNoSubscription = true;
    }

    handleGenericError();
  }
}

async function loadCurrentPlan(customerId: string): Promise<void> {
  try {
    const customerStore = useCustomerStore();

    if (customerStore.selected?.subscription) {
      const planUuid = customerStore.selected.subscription.planUuid;
      const planApi = await getPlan(planUuid);
      const plan = PlanTransformer.toDomain(planApi);

      if (hasValue(plan)) {
        customerStore.customers[customerId]!.currentPlan = plan;
      }

      if (isValidString(customerStore.selected.subscription.nextPlanUuid)) {
        const nextPlanApi = await getPlan(customerStore.selected.subscription.nextPlanUuid);

        customerStore.customers[customerId]!.nextPlan = hasValue(nextPlanApi)
          ? PlanTransformer.toDomain(nextPlanApi)
          : undefined;
      }

      customerStore.selected = customerStore.customers[customerId]!;
    }
  } catch (error: unknown) {
    handleGenericError();
  }
}

function handleGenericError(): void {
  const customerStore = useCustomerStore();

  customerStore.notification = {
    type: NotificationType.ERROR,
    message: "there_was_an_error_connecting_to_the_server_please_try_again_later",
    code: "generic error",
  };
}

export default fetchCustomerAH;
